import { registerBlock } from "../registry";
import { makeId } from "../helpers";
import type { ContentBlock, ContentTag, ArticleBody } from "../types";

// ─── Helper ─────────────────────────────────────────────────────────

function moonArticle(
  slug: string,
  opts: {
    title: string;
    time: string;
    tag: string;
    teaser: string;
    body: ArticleBody;
    sectionTags: ContentTag[];
  },
): ContentBlock<"article"> {
  return {
    id: makeId(`moon-article-${slug}`),
    kind: "article",
    tags: ["moon", ...opts.sectionTags],
    title: opts.title,
    subtitle: opts.teaser,
    readTimeMinutes: parseInt(opts.time, 10) || undefined,
    body: opts.body,
  };
}

// ─── Articles ───────────────────────────────────────────────────────

const embryoGrading = moonArticle("how-embryos-are-graded", {
  title: "Embryo grades: the numbers behind the classification",
  time: "7 min",
  tag: "Lab science",
  teaser: "Implantation rates by grade, and why day 5, 6, or 7 matters.",
  sectionTags: ["moon-science"],
  body: {
    introduction: "Lab science",
    quickSummary: [
      {
        title: "Visual assessment.",
        description:
          "Grading evaluates embryo appearance — expansion, inner cell mass quality, and trophectoderm quality.",
      },
      {
        title: "Grades aren't destiny.",
        description:
          "A 4AA blastocyst has the highest visual grade, but 3BB embryos result in healthy babies all the time.",
      },
      {
        title: "Subjectivity matters.",
        description:
          "Grading is subjective and varies between labs — the same embryo could get different grades at different clinics.",
      },
    ],
    sections: [
      {
        title: "Day 3 grading",
        content:
          "On day 3 after fertilization, embryologists evaluate the embryo based on cell count, fragmentation, and cell evenness. An ideal day 3 embryo has 6–10 cells that are roughly equal in size with minimal fragmentation. Fragmentation — small cellular debris between cells — is measured as a percentage. Less than 10% fragmentation is considered excellent, 10–25% is moderate, and above 25% is poor. However, day 3 grades are less predictive than day 5 grades, which is why most clinics now culture embryos to the blastocyst stage before making decisions.",
        bulletPoints: [
          "Ideal cell count: 6–10 cells by day 3",
          "Fragmentation below 10% is excellent",
          "Cell evenness (symmetry) is a positive indicator",
          "Day 3 grades are less predictive than day 5 grades",
        ],
      },
      {
        title: "Day 5–6 blastocyst grading explained",
        content:
          "Blastocyst grading uses a three-part system. The first is a number from 1 to 6 indicating expansion — how much the embryo has grown and expanded within (or hatched out of) its shell. A 1 is an early blastocyst with a small cavity; a 6 is fully hatched. The second is a letter A–C grading the inner cell mass (ICM), which becomes the baby. A means many tightly packed cells, B means several loosely grouped cells, and C means very few cells. The third is another letter A–C for the trophectoderm — the outer cell layer that becomes the placenta. A means many cells forming a cohesive layer, B means fewer cells, and C means very few large cells.",
        bulletPoints: [
          "Number 1–6: expansion and hatching status",
          "First letter A–C: inner cell mass (becomes the baby)",
          "Second letter A–C: trophectoderm (becomes the placenta)",
        ],
      },
      {
        title: "What makes a 'good' embryo",
        content:
          "Grades of 4AA, 4AB, and 4BA are all considered excellent — fully expanded blastocysts with strong ICM and trophectoderm scores. But don't dismiss lower grades. Embryos graded 3BB or even 3BC are still very transferable and result in healthy pregnancies regularly. The difference in per-transfer pregnancy rates between a 4AA and a 3BB is often smaller than patients expect.",
      },
      {
        title: "Grading vs PGT-A results",
        content:
          "Here's the surprising part: a beautiful-looking embryo can be chromosomally abnormal (aneuploid), and a rough-looking one can be perfectly normal (euploid). Appearance and genetics don't always correlate. This is one of the key arguments for PGT-A testing — it reveals information that the microscope simply cannot. If you're choosing between embryos for transfer, PGT-A results are generally more predictive than visual grade alone.",
      },
      {
        title: "What the data actually shows",
        content:
          "Per-transfer pregnancy rates do correlate with grade, but the differences are more nuanced than most patients realize. Top-grade blastocysts (4AA, 5AA) have implantation rates around 50–65%, while mid-grade embryos (3BB, 4BB) typically fall in the 35–50% range. Lower-grade embryos still have meaningful success rates. The takeaway: grade is one data point, not the whole story.",
        bulletPoints: [
          "Top-grade (4AA, 5AA): ~50–65% implantation rate",
          "Mid-grade (3BB, 4BB): ~35–50% implantation rate",
          "Lower grades still have meaningful success rates",
        ],
      },
      {
        title: "Day 5 vs day 6 vs day 7",
        content:
          "Embryos that reach blastocyst stage on day 5 tend to have slightly higher implantation rates than those that take until day 6 or day 7. However, day 6 blastocysts are still very viable and result in healthy pregnancies routinely. Day 7 blastocysts are less common and have lower success rates, but some clinics do culture and freeze them — and babies have been born from day 7 embryos. The speed of development is another data point, not a verdict.",
      },
    ],
  },
});

const implantation = moonArticle("implantation-explained", {
  title: "Implantation: what actually happens",
  time: "6 min",
  tag: "Biology",
  teaser: "The 72-hour window where everything comes together.",
  sectionTags: ["moon-science"],
  body: {
    introduction: "Biology",
    quickSummary: [
      {
        title: "A multi-day process.",
        description:
          "Implantation begins 6–10 days after ovulation and takes about 3 days to complete.",
      },
      {
        title: "Three distinct steps.",
        description:
          "The embryo must hatch from its shell, attach to the endometrium, and invade the lining — each step can fail independently.",
      },
      {
        title: "Failure is normal.",
        description:
          "Even with a perfect embryo and perfect lining, implantation fails about 30–40% of the time — this is normal biology, not a treatment failure.",
      },
    ],
    sections: [
      {
        title: "The implantation window",
        content:
          "The endometrium is only receptive to an embryo during a narrow window — typically days 20–24 of a natural 28-day cycle, or about 6–10 days after ovulation. Outside this window, the lining actively resists embryo attachment. This timing is controlled by progesterone, which transforms the endometrium from a proliferative state into a secretory, receptive one. In medicated FET cycles, doctors carefully time progesterone exposure to open this window at the right moment.",
      },
      {
        title: "Hatching",
        content:
          "Before an embryo can implant, it must first hatch out of the zona pellucida — the protective protein shell that has surrounded it since it was an egg. By day 5–6 of development, the expanding blastocyst naturally breaks through this shell. In IVF, embryologists sometimes perform 'assisted hatching' by making a small opening in the zona, especially for embryos with a thick shell or for patients over 38.",
      },
      {
        title: "Apposition and adhesion",
        content:
          "Once hatched, the embryo makes initial contact with the endometrial surface — a phase called apposition. The embryo loosely rests on the lining, orienting itself so that its trophectoderm faces the uterine wall. Then comes adhesion: molecular crosstalk between the embryo and the endometrium locks the embryo in place. This communication involves integrins, selectins, and cytokines — a complex molecular handshake that both sides must execute correctly.",
        bulletPoints: [
          "Apposition: embryo loosely positions itself on the lining",
          "Adhesion: molecular signals lock the embryo in place",
          "Both embryo and endometrium must participate in the signaling",
        ],
      },
      {
        title: "Invasion",
        content:
          "After adhesion, the trophoblast cells on the embryo's outer surface begin to burrow into the endometrial lining. These cells are remarkably aggressive — they invade the uterine wall, remodel maternal blood vessels, and establish the early blood supply that will become the placenta. This invasive process is what produces hCG (the pregnancy hormone), which is why home pregnancy tests don't turn positive until implantation is well underway.",
      },
      {
        title: "Why implantation fails",
        content:
          "Implantation failure is the single biggest source of pregnancy loss, and the most common cause is embryo chromosomal abnormality — the embryo simply doesn't have the genetic blueprint to develop further. Beyond that, receptivity timing mismatch (the window not being open when the embryo arrives), thin endometrial lining (below 7mm), and immune factors can all contribute. In many cases, the exact cause is never identified.",
        bulletPoints: [
          "Chromosomal abnormality is the #1 reason",
          "Receptivity timing mismatch between embryo and lining",
          "Thin lining (below 7mm) reduces implantation rates",
          "Immune factors — overactive or underactive immune response",
          "Often the exact cause is never identified",
        ],
      },
      {
        title: "What medicine can and can't control",
        content:
          "Doctors can optimize the endometrial lining with estrogen and progesterone, time the transfer window with precision, and select the best embryo available. What they can't control is the molecular conversation between embryo and endometrium, or whether a specific embryo has the chromosomal integrity to implant. This is why even perfectly executed transfers fail — and why a failed transfer doesn't mean something went wrong. It often means biology did what biology does.",
      },
    ],
  },
});

const ohss = moonArticle("ohss-explained", {
  title: "OHSS: what it is and how to avoid it",
  time: "6 min",
  tag: "Safety",
  teaser:
    "Understanding ovarian hyperstimulation — risks, prevention, and when to call your doctor.",
  sectionTags: ["moon-science"],
  body: {
    introduction: "Safety",
    quickSummary: [
      {
        title: "Usually mild.",
        description:
          "Mild OHSS is common (bloating, discomfort) and resolves on its own. Severe OHSS is rare but serious.",
      },
      {
        title: "Actively managed.",
        description:
          "Your doctor monitors for OHSS risk and can adjust your protocol to prevent it.",
      },
      {
        title: "Modern protocols help.",
        description:
          "Freeze-all with a GnRH agonist trigger has dramatically reduced severe OHSS in modern IVF.",
      },
    ],
    sections: [
      {
        title: "What OHSS is",
        content:
          "Ovarian hyperstimulation syndrome (OHSS) occurs when the ovaries over-respond to fertility medications. The ovaries swell, and fluid shifts from blood vessels into the abdominal cavity (and sometimes the chest). In mild cases, this causes bloating and discomfort that resolves within a week. In severe cases — which are rare with modern protocols — it can cause dangerous complications requiring hospitalization.",
      },
      {
        title: "Mild vs moderate vs severe symptoms",
        content:
          "OHSS exists on a spectrum. Recognizing where you fall helps you know when to call your clinic and when to head to the ER.",
        bulletPoints: [
          "Mild: bloating, mild abdominal discomfort, slight weight gain (most common, resolves in days)",
          "Moderate: nausea, vomiting, abdominal distension, 5+ pounds of weight gain over 1–2 days",
          "Severe: difficulty breathing, severe abdominal pain, blood clots, decreased urination, kidney dysfunction (rare — requires immediate medical attention)",
        ],
      },
      {
        title: "Who's at risk",
        content:
          "Certain factors make OHSS more likely. Your doctor assesses these before starting stimulation and adjusts the protocol accordingly. Being at higher risk doesn't mean you'll develop OHSS — it means your care team will monitor you more closely and take preventive steps.",
        bulletPoints: [
          "Young age (under 35)",
          "Low BMI",
          "PMOS (ex PCOS) diagnosis",
          "High AMH levels (above 3.5 ng/mL)",
          "High antral follicle count (above 20)",
          "High estradiol levels during stimulation",
          "Previous history of OHSS",
        ],
      },
      {
        title: "How doctors prevent it",
        content:
          "Prevention is the most important part of OHSS management, and modern reproductive endocrinologists have several tools available. The biggest game-changer has been the GnRH agonist trigger (Lupron trigger) instead of the traditional hCG trigger. hCG is what drives OHSS — it stimulates the ovaries to continue producing fluid and hormones after retrieval. A Lupron trigger avoids this entirely.",
        bulletPoints: [
          "Antagonist protocol with GnRH agonist (Lupron) trigger instead of hCG",
          "Freeze-all cycle — avoids pregnancy-related hCG that worsens OHSS",
          "Lower medication doses from the start for high-risk patients",
          "Coasting — stopping stimulation medications for 1–2 days before trigger",
          "Cabergoline — a dopamine agonist that reduces vascular permeability",
        ],
      },
      {
        title: "What to do if you have symptoms",
        content:
          "After retrieval, some bloating and discomfort is normal — most patients experience mild symptoms. But you should call your clinic if you gain more than 2 pounds in a day, have persistent nausea or vomiting, or notice your abdomen is significantly distended. Go to the ER if you have difficulty breathing, severe pain, or decreased urination. Don't wait to see if it gets better on its own — early intervention makes a significant difference.",
      },
      {
        title: "The good news",
        content:
          "Severe OHSS has dropped dramatically with modern protocols. The combination of antagonist protocols, Lupron triggers, and freeze-all strategies has made severe OHSS increasingly rare at experienced clinics. Many high-volume centers report rates of severe OHSS below 1%. If your doctor identifies you as high-risk, the protocol adjustments described above are highly effective at keeping you safe while still achieving a good egg yield.",
      },
    ],
  },
});

// ─── Register all Science articles ──────────────────────────────────

const allNewScience = [embryoGrading, implantation, ohss];

for (const article of allNewScience) {
  registerBlock(article);
}
